import React, { useEffect, useState } from 'react';
import { FileText, Loader2, Trash2, Upload } from 'lucide-react';
import { AppointmentDocument, AppointmentDocumentType } from '@/types';
import { api } from '@/services/api';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { formatUkDateTime } from '@/lib/dates';
import { normalizeErrorMessage } from '@/lib/errorUtils';

const DOCUMENT_TYPE_OPTIONS: Array<{ value: AppointmentDocumentType; label: string }> = [
  { value: 'audiogram', label: 'Audiogram' },
  { value: 'referral', label: 'Referral' },
  { value: 'report', label: 'Report' },
  { value: 'other', label: 'Other' }
];

const documentTypeLabel = (type: string): string =>
  DOCUMENT_TYPE_OPTIONS.find((o) => o.value === type)?.label || 'Document';

interface AppointmentDocumentsPanelProps {
  appointmentId: string;
}

export const AppointmentDocumentsPanel: React.FC<AppointmentDocumentsPanelProps> = ({
  appointmentId
}) => {
  const [documents, setDocuments] = useState<AppointmentDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [documentType, setDocumentType] = useState<AppointmentDocumentType>('audiogram');

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    api
      .listAppointmentDocuments(appointmentId)
      .then((items) => {
        if (isActive) setDocuments(items);
      })
      .catch(() => {
        if (isActive) setDocuments([]);
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [appointmentId]);

  const handleUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const doc = await api.uploadAppointmentDocument(file, appointmentId, documentType);
      setDocuments((prev) => [doc, ...prev]);
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to upload document.'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = async (doc: AppointmentDocument) => {
    const confirmed = await showConfirmModal(
      `Remove "${doc.fileName}"? The file will be deleted.`,
      { title: 'Remove Document', confirmText: 'Remove', isDestructive: true }
    );
    if (!confirmed) return;
    setRemovingId(doc.id);
    try {
      await api.removeAppointmentDocument(doc.id);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to remove document.'));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">
          Documents
        </p>
        <div className="flex items-center gap-2">
          <select
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value as AppointmentDocumentType)}
            disabled={isUploading}
            className="text-xs text-slate-700 border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-400"
          >
            {DOCUMENT_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <label
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${isUploading ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 cursor-pointer'}`}
          >
            {isUploading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Upload className="w-3.5 h-3.5" />
            )}
            {isUploading ? 'Uploading…' : 'Upload'}
            <input
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              disabled={isUploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUpload(file);
                e.target.value = '';
              }}
            />
          </label>
        </div>
      </div>
      {isLoading ? (
        <p className="text-xs text-slate-400 italic">Loading documents…</p>
      ) : documents.length === 0 ? (
        <p className="text-xs text-slate-500 italic">
          No documents uploaded. Attach audiograms, referrals or reports (images or PDF, max 10
          MB).
        </p>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="bg-slate-50 border border-slate-100 rounded-lg p-2.5 flex flex-wrap items-center justify-between gap-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="bg-teal-100 text-teal-700 text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-widest border border-teal-200 shrink-0">
                  {documentTypeLabel(doc.documentType)}
                </span>
                <a
                  href={doc.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-teal-700 hover:text-teal-900 transition-colors truncate"
                >
                  <FileText className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{doc.fileName || 'View document'}</span>
                </a>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-[10px] text-slate-400">
                  {formatUkDateTime(doc.createdAt, '—')}
                </span>
                <button
                  type="button"
                  disabled={removingId === doc.id}
                  onClick={() => handleRemove(doc)}
                  className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-red-500 hover:text-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {removingId === doc.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3" />
                  )}
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
